import { useEffect, useState } from 'react';

import { toHouseStyle, type HouseStyle } from '@/entities/tenant';
import { errorMessage } from '@/shared/api';

import { BrandingCard } from '../components/BrandingCard';
import { CoverPreviewCard } from '../components/CoverPreviewCard';
import { brandingCopy } from '../copy';
import { useBranding, useTenant, useUpdateHouseStyle, useUpdateLogo } from '../hooks';
import { SettingsSection } from './TabLayout';

/**
 * Tabblad Huisstijl: logo en kleuren, met het voorbeeld van voorblad en
 * klantpagina ernaast.
 *
 * Op een breed scherm staan ze naast elkaar en blijft het voorbeeld in beeld
 * terwijl je door de kleuren scrolt: een keuze is meteen te zien in wat de klant
 * krijgt. Smaller staat het voorbeeld onder de kiezer. Het voorbeeld beweegt mee
 * met wat er in de kiezer staat, ook als dat nog niet bewaard is.
 */
export function BrandingTab({ canManage }: { canManage: boolean }) {
    const tenant = useTenant();
    const branding = useBranding();
    const updateHouseStyle = useUpdateHouseStyle();
    const updateLogo = useUpdateLogo();

    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    /* De kleuren zoals ze in de kiezer staan, zodat het voorbeeld ernaast meebeweegt. */
    const [houseStyle, setHouseStyle] = useState<HouseStyle | null>(null);

    /* De object-URL van een gekozen logo moet weer vrijgegeven worden. */
    useEffect(() => {
        return () => {
            if (logoPreview) {
                URL.revokeObjectURL(logoPreview);
            }
        };
    }, [logoPreview]);

    const companyName = tenant.data?.name ?? '';
    const error = updateLogo.error ?? updateHouseStyle.error;
    const previewStyle = houseStyle ?? (branding.data ? toHouseStyle(branding.data) : null);
    const uploadingLogo = updateLogo.isPending ? logoPreview : null;

    return (
        <SettingsSection
            title={brandingCopy.title}
            query={branding}
            onRetry={() => void branding.refetch()}
        >
            {branding.data && previewStyle ? (
                <div className="grid max-w-[1120px] items-start gap-[14px] @min-[860px]:grid-cols-[minmax(0,1fr)_minmax(320px,400px)]">
                    <BrandingCard
                        branding={branding.data}
                        companyName={companyName}
                        canManage={canManage}
                        logoPreview={uploadingLogo}
                        uploading={updateLogo.isPending}
                        savingHouseStyle={updateHouseStyle.isPending}
                        notice={notice}
                        error={error ? errorMessage(error) : null}
                        onLogoSelected={(file) => {
                            setNotice(null);
                            updateHouseStyle.reset();
                            setLogoPreview(URL.createObjectURL(file));
                            updateLogo.mutate(file, {
                                onSuccess: () => setNotice(brandingCopy.logoSaved),
                            });
                        }}
                        onLogoRemove={() => {
                            setNotice(null);
                            updateHouseStyle.reset();
                            updateLogo.mutate(null, {
                                onSuccess: () => setNotice(brandingCopy.logoRemoved),
                            });
                        }}
                        onHouseStyleChange={(style) => {
                            setNotice(null);
                            setHouseStyle(style);
                        }}
                        onHouseStyleSave={(style) => {
                            setNotice(null);
                            updateLogo.reset();
                            updateHouseStyle.mutate(style, {
                                onSuccess: () => setNotice(brandingCopy.saved),
                            });
                        }}
                    />

                    {/* Blijft staan op de hoogte van de bovenrand van het werkvlak. */}
                    <div className="@min-[860px]:sticky @min-[860px]:top-[22px]">
                        <CoverPreviewCard
                            branding={branding.data}
                            houseStyle={previewStyle}
                            logoPreview={uploadingLogo}
                            companyName={companyName}
                        />
                    </div>
                </div>
            ) : null}
        </SettingsSection>
    );
}
